import { useCallback } from "react"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { GetSystemAssetGroupsDocument } from "@hydrologiq/gql-experience/graphql"
import { useSectionEvent, SECTION_EVENTS } from "@hydrologiq/shared/hooks/useSectionEvent"

export interface AssetGroupChild {
  assetId: string
  assetName: string
  monitoringStatus: string
}

export interface AssetGroup {
  parentAssetId: string
  parentAssetName: string
  children: AssetGroupChild[]
}

export interface UseAssetGroupsReturn {
  groups: AssetGroup[]
  loading: boolean
  error: string | undefined
  refetch: () => void
}

export function useAssetGroups(systemId: string): UseAssetGroupsReturn {
  const { data, loading, error, refetch } = useQueryWithEndpoint(GetSystemAssetGroupsDocument, {
    variables: { systemId },
    skip: !systemId,
  }, "primary")

  // Refetch when monitoring status changes (e.g. activating/deactivating connections)
  const handleMonitoringChange = useCallback(() => {
    refetch()
  }, [refetch])
  useSectionEvent(SECTION_EVENTS.MONITORING_STATUS_CHANGED, handleMonitoringChange)

  const groups: AssetGroup[] = data?.getSystemAssetGroups ?? []

  return {
    groups,
    loading,
    error: error?.message,
    refetch,
  }
}
